import { useAuthorization } from 'components/Authorization';
import { useCurrentConversationIdValue } from '../conversation';
import { useKnowledgeQueryCount } from './useKnowledgeQuery';
import {
  isInTimeRange,
  useCurrentQAPairKnowledgeQuery,
  useIsQAPairTargetMessage,
  useQAPairAICanNotReply,
  useQAPairAutoPopupDisabled,
  useQAPairVisible,
  useSetQAPairMeta,
} from './useQAPair';
import {
  quitCreateSupplementKnowledge,
  submitSupplementKnowledge,
} from 'services/api/supplementKnowledge';
import { eventBus } from 'services/eventBus';
import { useMultitabState } from 'views/Multitab/state';
import { useUpdateQAPairRecord } from './useQAPairRecord';
import { useMemoizedFn } from 'ahooks';
import { isInQaPairMsgRang } from './useQaPairMsgRang';
import { formatLoggerData, logger } from 'services/logger';
import { queueMap } from './queueMap';
import { isIdEqual } from 'utils/is';
import { isNil, toNumber, uniq } from 'lodash-es';
import { isNimCacheId } from './getNimCacheId';
import { captureDuration } from './qaPairTimestamp';
import { refreshScenario } from 'views/Multitab/channel';
import { useAfterSubmitClearKnowledgeQuery, useCleanQAPair } from './useCleanQAPair';
import { useMessageList } from '../message';
import { useSelectedAnswerMsgIds, useSetSelectedAnswerMsgIds } from './useMessageSelect';

/**
 * 延迟上报QAPair动画打点
 */
const useDelayTrackTriggerPopup = () => {
  const { getKnowledgeQueryCount } = useKnowledgeQueryCount();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const delayTrackTriggerPopup = async ({
    queryMsgId,
    conversationId,
    impressions,
    msg,
    currentQaPairVisible,
  }: {
    queryMsgId: string;
    conversationId: string;
    impressions: number;
    msg: Conversation.Message;
    currentQaPairVisible: boolean;
  }) => {
    const assistantId = payload?.id;
    try {
      const msgStr = JSON.stringify(msg);
      const body = {
        query_msg_id: queryMsgId,
        assistant_id: assistantId,
        popup_times: impressions,
        timestamp: Date.now(),
        trigger_popup: true,
        trigger_popup_msg: msgStr,
      };
      setTimeout(async () => {
        const count = await getKnowledgeQueryCount(conversationId);
        quitCreateSupplementKnowledge({
          ...body,
          popup_times: count,
        }).then(() => {
          if (currentQaPairVisible) {
            eventBus.emit('CONFIRM_QAPAIR_MODAL_EXISTS', {
              visible: currentQaPairVisible,
              msg: msgStr,
            });
          }
        });
      }, 1000);
    } catch (err: any) {}
  };

  return delayTrackTriggerPopup;
};

/**
 * 判断消息是否符合qa条件
 */
export const useCheckQAPair = () => {
  const qaPairVisible = useQAPairVisible();
  const setQAPairMeta = useSetQAPairMeta();
  const submitAfterClearKnowledgeQuery = useAfterSubmitClearKnowledgeQuery();
  const { increaseKnowledgeQueryCount, getKnowledgeQueryCount } = useKnowledgeQueryCount();
  const aiCanNotReplyPermission = useQAPairAICanNotReply();
  const currentConversationId = useCurrentConversationIdValue();
  const messageList = useMessageList();
  const delayTrackTriggerPopup = useDelayTrackTriggerPopup();
  const cleanQAPair = useCleanQAPair();
  const { activeKey } = useMultitabState();
  const isTargetMessage = useIsQAPairTargetMessage();

  const selectedAnswerMsgIds = useSelectedAnswerMsgIds('', true);
  const setSelectedAnswerMsgIds = useSetSelectedAnswerMsgIds('', true);

  const currentQAPairKnowledgeQuery = useCurrentQAPairKnowledgeQuery();
  const autoPopupDisabled = useQAPairAutoPopupDisabled();

  const updateQAPairRecord = useUpdateQAPairRecord();

  // 插入QA Pair消息
  const insertQAPairMsg = useMemoizedFn(async (msg: Conversation.Message) => {
    if (autoPopupDisabled) {
      return;
    }
    const { conversationId } = msg;
    if (aiCanNotReplyPermission && isTargetMessage(msg) && currentQAPairKnowledgeQuery) {
      const queryMsgId = currentQAPairKnowledgeQuery.msgId;
      const isRange = isInQaPairMsgRang(msg, currentQAPairKnowledgeQuery);
      const isOnQueue = queueMap.has(conversationId);
      const enable = !isOnQueue && isRange;
      logger.info(
        `[useCheckQAPair][insertQAPairMsg]currentConversationId:${currentConversationId} conversationId:${conversationId} queryMsgId:${queryMsgId} msgId:${msg.msgId} isOnQueue:${isOnQueue} isShowing:${qaPairVisible} isRange:${isRange} enable:${enable}`,
      );
      const isOnCurrentConversation = isIdEqual(currentConversationId, conversationId);
      if (enable && isOnCurrentConversation) {
        const impressions = await getKnowledgeQueryCount(conversationId);
        logger.info(
          `[useCheckQAPair][insertQAPairMsg] conversationId:${conversationId} queryMsgId:${queryMsgId} msgId:${msg.msgId} impressions:${impressions}`,
        );
        if (isNil(impressions)) {
          logger.info(
            `[useCheckQAPair][insertQAPairMsg] conversationId:${conversationId} queryMsgId:${queryMsgId} msgId:${msg.msgId} impressions is nil`,
          );
          return;
        }
        if (impressions <= 3) {
          logger.info(
            `[useCheckQAPair][insertQAPairMsg] conversationId:${conversationId} queryMsgId:${queryMsgId} msgId:${msg.msgId} impressions:${impressions} activeKey:${activeKey}`,
          );
          if (!activeKey) {
            logger.info(
              `[useCheckQAPair][insertQAPairMsg] conversationId:${conversationId} queryMsgId:${queryMsgId} msgId:${msg.msgId} impressions:${impressions} updateMessage`,
            );
            const filteredMsgIds: string[] = [];
            messageList.forEach((item) => {
              const inTimeRange = isInTimeRange(item.sendTime, msg.sendTime);
              if (
                isIdEqual(item.conversationId, conversationId) &&
                isTargetMessage(item) &&
                inTimeRange &&
                isInQaPairMsgRang(item, currentQAPairKnowledgeQuery)
              ) {
                filteredMsgIds.push(item.msgId);
              }
            });
            delayTrackTriggerPopup({
              queryMsgId,
              conversationId,
              impressions,
              msg,
              currentQaPairVisible: qaPairVisible,
            });
            const merged = [
              ...Array.from(selectedAnswerMsgIds ?? []),
              ...(filteredMsgIds ?? []),
              msg.msgId,
            ];
            const uniqList = uniq(merged);
            if (uniqList.length > 0) {
              if (!qaPairVisible) {
                // 如果QA Pair未显示，则增加知识查询次数
                increaseKnowledgeQueryCount(conversationId);
              }
              setSelectedAnswerMsgIds(new Set(uniqList));
              console.log('setQAPairMeta from useCheckQAPair');
              setQAPairMeta((prev) =>
                prev.visible
                  ? prev
                  : {
                      ...prev,
                      visible: true,
                      source: 'CUSTOMER',
                      selectMode: 'single',
                      rangeTimestamp: toNumber(currentQAPairKnowledgeQuery.sendTime),
                      defaultKnowledgeQueryMessage: currentQAPairKnowledgeQuery,
                      isActiveShow: true,
                    },
              );
              requestAnimationFrame(() => {
                eventBus.emit('SCROLL_TO_MSG_BY_ID', msg.msgId);
              });
            }
          }
        }
      }
    }
  });

  const replaceQAPairMsg = useMemoizedFn((localMsgId: string, msg: Conversation.Message) => {
    if (!aiCanNotReplyPermission || !currentQAPairKnowledgeQuery) {
      return;
    }

    const { conversationId } = msg;
    setSelectedAnswerMsgIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(localMsgId);
      newSet.add(msg.msgId);
      return newSet;
    });
    const queueItem = queueMap.get(conversationId);
    if (queueItem) {
      // 从 queueItem 的 answers 中查找 messageId = localMsgId, 将其替换为 msg对象的msgId
      const targetAnswer = queueItem.supplementalKnowledgeValue.originAnswers.find(
        (item) => item.messageId === localMsgId,
      );
      if (targetAnswer) {
        logger.info(
          `[useCheckQAPair][replaceQAPairMsg]replace queueItem answer ${formatLoggerData({
            localMsgId,
            msg,
          })}`,
        );
        targetAnswer.messageId = msg.msgId;
      }
    }
  });

  const replaceQAPairMsgAfterAck = useMemoizedFn(
    (localMsgId: string, msg: Conversation.Message) => {
      if (!aiCanNotReplyPermission || !currentQAPairKnowledgeQuery) {
        return;
      }

      replaceQAPairMsg(localMsgId, msg);
      requestAnimationFrame(() => {
        const conversationId = msg?.conversationId;
        const body = queueMap.get(conversationId);
        if (conversationId && body) {
          const isAllSuccess = body.supplementalKnowledgeValue.originAnswers.every(
            (item) => item.messageId && !isNimCacheId(item.messageId),
          );
          if (isAllSuccess) {
            captureDuration(conversationId, '$submitSupplementKnowledgeDuration', true);
            cleanQAPair();
            submitSupplementKnowledge(body)
              .then(() => {
                queueMap.delete(conversationId);
                const { supplementalKnowledgeValue } = body;
                const queryMsgIds = supplementalKnowledgeValue.originQuestions
                  .map((item) => item.messageId)
                  .filter((item) => item) as string[];
                updateQAPairRecord(
                  supplementalKnowledgeValue.originQuestions,
                  supplementalKnowledgeValue.originAnswers,
                );
                return submitAfterClearKnowledgeQuery(queryMsgIds);
              })
              .then(() => {
                refreshScenario('supplementKnowledge');
              })
              .catch((err: any) => {
                logger.error(
                  `replaceQAPairMsgAfterAck createSupplementKnowledge ${formatLoggerData({
                    errMsg: err?.message || '',
                    body,
                  })}`,
                );
              })
              .finally(() => {
                queueMap.delete(conversationId);
              });
          }
        }
      });
    },
  );

  return {
    insertQAPairMsg,
    replaceQAPairMsg,
    replaceQAPairMsgAfterAck,
  };
};
